import { useCallback, useEffect, RefObject } from 'react';

interface UseAutoResizeTextareaOptions {
  minRows?: number;
  maxRows?: number;
}

/**
 * Hook to auto-resize a textarea based on content while preserving scroll position.
 * When typing at the end of the textarea, allows natural scroll to keep cursor visible.
 * When typing in the middle, preserves the current scroll position to prevent jumps.
 */
export function useAutoResizeTextarea(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  value: string,
  options: UseAutoResizeTextareaOptions = {}
) {
  const { minRows, maxRows } = options;

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Check if cursor is at the end of the text - if so, allow natural scroll behavior
    const isAtEnd = textarea.selectionStart === textarea.value.length;

    // Save scroll position before height adjustment
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    // Get current height before any changes
    const currentHeight = textarea.offsetHeight;

    // Create a hidden clone to measure the required height without affecting layout
    const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.width = `${textarea.offsetWidth}px`;
    clone.style.minHeight = '0';
    clone.style.maxHeight = 'none';
    document.body.appendChild(clone);

    let newHeight = clone.scrollHeight;

    // Apply min/max constraints if provided
    if (minRows !== undefined || maxRows !== undefined) {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
      const paddingTop = parseInt(getComputedStyle(textarea).paddingTop) || 0;
      const paddingBottom = parseInt(getComputedStyle(textarea).paddingBottom) || 0;

      if (minRows !== undefined) {
        const minHeight = lineHeight * minRows + paddingTop + paddingBottom;
        newHeight = Math.max(newHeight, minHeight);
      }

      if (maxRows !== undefined) {
        const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom;
        newHeight = Math.min(newHeight, maxHeight);
        // Enable scrolling if content exceeds maxHeight
        textarea.style.overflowY = clone.scrollHeight > maxHeight ? 'auto' : 'hidden';
      }
    }

    // Remove the clone
    document.body.removeChild(clone);

    // Only update height if it changed
    if (newHeight !== currentHeight) {
      textarea.style.height = `${newHeight}px`;
    }

    // Restore scroll position if not at end
    if (!isAtEnd) {
      // Use requestAnimationFrame to restore after paint
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    }
  }, [textareaRef, minRows, maxRows]);

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return { adjustHeight };
}
